import React from 'react';
import { Flag, Diamond, Hourglass } from 'lucide-react';
import { iniciales } from './helpers';

// Una bandera (flag) con color/intensidad varía con prioridad — patrón estándar (Linear/Jira)
// que el ojo lee inmediatamente: urgente roja sólida, alta naranja, media slate, baja outline.
// fill aplicado al estilo inline porque algunos navegadores ignoran el atributo `fill` en SVG
// si lucide define stroke-only por default.
const flagFor = (prioridad, size, strokeWidth = 2) => {
  switch (prioridad) {
    case 'urgente': return <Flag size={size} strokeWidth={strokeWidth} className="text-rose-500"   style={{ fill: 'currentColor' }} />;
    case 'alta':    return <Flag size={size} strokeWidth={strokeWidth} className="text-orange-500" style={{ fill: 'currentColor' }} />;
    case 'media':   return <Flag size={size} strokeWidth={strokeWidth} className="text-slate-500"  style={{ fill: 'currentColor' }} />;
    case 'baja':    return <Flag size={size} strokeWidth={strokeWidth} className="text-slate-400" />;
    default:        return null;
  }
};

// Indicador minimalista de prioridad: solo el ícono, sin fondo.
// Usado en filas de lista, mis tareas, calendario.
// compact=true reduce 1px (para paneles embebidos en otras fichas).
export const PriorityIcon = ({ prioridad, compact = false }) => {
  const size = compact ? 10 : 11;
  const flag = flagFor(prioridad, size);
  if (!flag) return <span className="w-2.5 h-2.5" />;
  return flag;
};

// Versión con fondo (badge cuadrado). Usado en cards del board.
// Todas las prioridades llevan fondo para mantener spacing consistente; la
// intensidad del color crea la jerarquía visual.
export const PriorityBadge = ({ prioridad }) => {
  const bgMap = {
    urgente: 'bg-rose-50',
    alta:    'bg-orange-50',
    media:   'bg-slate-100',
    baja:    'bg-slate-50',
  };
  const labelMap = {
    urgente: 'Urgente', alta: 'Alta', media: 'Media', baja: 'Baja',
  };
  const flag = flagFor(prioridad, 11);
  if (!flag) return null;
  return (
    <span
      title={labelMap[prioridad]}
      className={`inline-flex items-center justify-center w-4 h-4 rounded ${bgMap[prioridad]}`}
    >
      {flag}
    </span>
  );
};

// Etiqueta de fecha relativa: "Hoy", "Mañana", "Ayer" o fecha corta.
// Pinta rose si vencida, amber si dentro de 2 días, slate si futura.
// compact=true usa text-[10px] (para paneles embebidos en otras fichas).
// `espera`: lista de codigos de predecesoras abiertas (tarea congelada). La
// fecha no corre mientras espera, asi que se muestra "En espera" en lugar de
// pintarla vencida.
export const FechaText = ({ fecha, compact = false, espera = null }) => {
  if (espera && espera.length > 0) {
    const size = compact ? 'text-[10px]' : 'text-xs';
    return (
      <span
        className={`${size} inline-flex items-center gap-1 text-indigo-500 whitespace-nowrap`}
        title={`Arranca cuando se cierre ${espera.join(', ')}`}
      >
        <Hourglass size={compact ? 9 : 11} strokeWidth={2} />
        En espera
      </span>
    );
  }
  if (!fecha) return <span className="text-slate-300">—</span>;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(fecha + 'T00:00:00');
  const diff = Math.floor((d - today) / 86400000);

  let cls = 'text-slate-500';
  let label = d.toLocaleDateString('es-VE', { day: 'numeric', month: 'short' });
  if (diff < 0) cls = 'text-rose-600 font-medium';
  else if (diff <= 2) cls = 'text-amber-600 font-medium';
  if (diff === 0) label = 'Hoy';
  if (diff === 1) label = 'Mañana';
  if (diff === -1) label = 'Ayer';

  const size = compact ? 'text-[10px]' : 'text-xs';
  return <span className={`${size} tabular-nums ${cls}`}>{label}</span>;
};

// Color por defecto de un hito (milestone) si no tiene uno propio. Violeta para
// diferenciarlo de cyan (en curso), emerald (hecho), rose (vencido), slate.
export const HITO_COLOR_DEFAULT = '#8b5cf6';

// Avatar individual con iniciales. Tint del color del proyecto (nativo del modulo).
// ring=true agrega anillo blanco para separar avatares cuando se apilan.
export const AsignadoAvatar = ({ nombre, color = '#06b6d4', size = 20, ring = false }) => (
  <span
    title={nombre}
    className={`inline-flex items-center justify-center rounded-full font-semibold flex-shrink-0 ${ring ? 'ring-2 ring-white' : ''}`}
    style={{
      width: size,
      height: size,
      fontSize: size <= 20 ? 9 : 10,
      // Fondo opaco: en los stacks los avatares se enciman y un fondo
      // translucido deja ver las iniciales del de al lado.
      background: `linear-gradient(${color}1f, ${color}1f), #fff`,
      color,
    }}
  >
    {iniciales(nombre)}
  </span>
);

// Avatares apilados (overlap -ml-1.5). Muestra hasta `max` + pill "+N".
// asignados: [{id, nombre}]. Devuelve null si no hay ninguno.
export const AsignadosStack = ({ asignados = [], color = '#06b6d4', size = 20, max = 3 }) => {
  if (!asignados || asignados.length === 0) return null;
  const visibles = asignados.slice(0, max);
  const extra = asignados.length - visibles.length;
  return (
    <div className="flex items-center">
      {visibles.map((a, i) => (
        <div key={a.id ?? i} className={i > 0 ? '-ml-1.5' : ''} style={{ zIndex: visibles.length - i }}>
          <AsignadoAvatar nombre={a.nombre} color={color} size={size} ring />
        </div>
      ))}
      {extra > 0 && (
        <span
          className="-ml-1.5 inline-flex items-center justify-center rounded-full ring-2 ring-white bg-slate-100 text-slate-500 font-semibold flex-shrink-0 tabular-nums"
          style={{ width: size, height: size, fontSize: size <= 20 ? 9 : 10, zIndex: 0 }}
          title={asignados.slice(max).map((a) => a.nombre).join(', ')}
        >
          +{extra}
        </span>
      )}
    </div>
  );
};

// Estado visual de un hito segun cumplimiento + fecha.
// -> { key: 'completado'|'vencido'|'pendiente', vencido: bool }
export const hitoEstado = (hito) => {
  if (!hito) return { key: 'pendiente', vencido: false };
  if (hito.completado) return { key: 'completado', vencido: false };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const f = hito.fecha ? new Date(hito.fecha + 'T00:00:00') : null;
  const vencido = !!(f && f < today);
  return { key: vencido ? 'vencido' : 'pendiente', vencido };
};

// Chip de hito para tarjetas/filas. Usa Diamond (rombo) para diferenciarlo del
// Flag de prioridad. Relleno si cumplido. Rose si vencido y pendiente.
export const HitoChip = ({ hito }) => {
  if (!hito) return null;
  const { vencido } = hitoEstado(hito);
  const c = vencido ? '#e11d48' : (hito.color || HITO_COLOR_DEFAULT);
  return (
    <span
      title={hito.nombre}
      className="inline-flex items-center gap-1 pl-1.5 pr-1.5 py-px rounded text-[9px] font-medium leading-none max-w-[120px]"
      style={{ backgroundColor: `${c}1f`, color: c }}
    >
      <Diamond size={8} strokeWidth={2.5} style={{ fill: hito.completado ? 'currentColor' : 'none' }} />
      <span className="truncate">{hito.nombre}</span>
    </span>
  );
};
